import type { CaptureResult, PostHogConfig } from 'posthog-js';
import type { RouteObject } from 'react-router';
import { z } from 'zod';

const runtimeSchema = z.object({
   customer: z.string().regex(/^[a-z0-9]$|^[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/),
   runtimeEnv: z.enum(['production', 'development', 'local']),
   projectKey: z.string().trim().min(1),
});

type AnalyticsStart = Readonly<{
   projectKey: string;
   origin: string;
   labels: Readonly<{ customer_instance: string; environment: 'production' | 'demo' }>;
}>;

const allowedEvents = new Set(['$pageview', '$pageleave', '$web_vitals', '$autocapture', '$rageclick', '$dead_click', '$$heatmap']);

const allowedProperties = new Set([
   'token',
   'distinct_id',
   '$cookieless_mode',
   '$process_person_profile',
   '$lib',
   '$lib_version',
   '$insert_id',
   '$raw_user_agent',
   '$host',
   '$timezone',
   '$pathname',
   '$current_url',
   '$browser',
   '$browser_version',
   '$os',
   '$os_version',
   '$device_type',
   '$browser_language',
   '$screen_width',
   '$screen_height',
   '$viewport_width',
   '$viewport_height',
   '$referring_domain',
   '$prev_pageview_pathname',
   '$prev_pageview_duration',
   '$prev_pageview_last_scroll',
   '$prev_pageview_last_scroll_percentage',
   '$prev_pageview_max_scroll',
   '$prev_pageview_max_scroll_percentage',
   '$prev_pageview_last_content',
   '$prev_pageview_last_content_percentage',
   '$prev_pageview_max_content',
   '$prev_pageview_max_content_percentage',
   '$web_vitals_CLS_value',
   '$web_vitals_FCP_value',
   '$web_vitals_INP_value',
   '$web_vitals_LCP_value',
   '$event_type',
   '$elements_chain',
   '$dead_click_scroll_delay_ms',
   '$dead_click_mutation_delay_ms',
   '$dead_click_absolute_delay_ms',
   '$dead_click_selection_changed_delay_ms',
   '$dead_click_visibility_changed_delay_ms',
   '$dead_click_focus_changed_delay_ms',
   '$dead_click_scroll_timeout',
   '$dead_click_mutation_timeout',
   '$dead_click_absolute_timeout',
   '$dead_click_selection_changed_timeout',
   '$dead_click_visibility_changed_timeout',
   '$heatmap_data',
]);

const referringDomainPattern = /^(?:\$direct|[\d.:a-z[\]-]+)$/;

const heatmapPointSchema = z.object({ x: z.number(), y: z.number(), target_fixed: z.boolean(), type: z.string() });

const heatmapDataSchema = z.record(z.string(), z.array(heatmapPointSchema));

const privacyConfig = {
   api_host: 'https://eu.i.posthog.com',
   defaults: '2026-08-30',
   cookieless_mode: 'always',
   person_profiles: 'never',
   mask_all_text: true,
   mask_all_element_attributes: true,
   capture_heatmaps: true,
   capture_dead_clicks: true,
   capture_performance: { web_vitals: true, web_vitals_attribution: false },
   advanced_disable_flags: true,
   disable_external_dependency_loading: true,
} satisfies Partial<PostHogConfig>;

const excludedDomains = ['dev.envoria.app', 'lab.envoria.app', 'local.envoria.app', 'localhost'];

let initialization: Promise<void> | undefined;

/** Starts cookieless, route-only analytics once per document. The promise never rejects. */
export function initAnalytics(input: Readonly<{ customer: unknown; runtimeEnv: unknown; projectKey: unknown }>): Promise<void> {
   if (initialization) {
      return initialization;
   }

   const runtime = runtimeSchema.safeParse(input);
   const start = runtime.success ? findAnalyticsStart(runtime.data) : null;

   if (!start) {
      return Promise.resolve();
   }

   initialization = startAnalytics(start).catch(() => undefined);

   return initialization;
}

function findAnalyticsStart({ customer, runtimeEnv, projectKey }: z.output<typeof runtimeSchema>): AnalyticsStart | null {
   if (process.env.NODE_ENV !== 'production' || typeof window === 'undefined') {
      return null;
   }

   const { protocol, origin } = window.location;
   const hostname = window.location.hostname.toLowerCase().replace(/\.$/, '');

   if (protocol !== 'https:' || runtimeEnv === 'local' || excludedDomains.some((domain) => isWithinDomain(hostname, domain))) {
      return null;
   }

   // Customer demo instances also run on their own domains, such as csrd.dev.haniel.de.
   const environment = runtimeEnv === 'production' ? 'production' : 'demo';

   return { projectKey, origin, labels: { customer_instance: customer, environment } };
}

function isWithinDomain(hostname: string, domain: string) {
   return hostname === domain || hostname.endsWith(`.${domain}`);
}

async function startAnalytics(start: AnalyticsStart) {
   const [{ default: posthog }, { matchRoutes }, { default: generateRoutes }] = await Promise.all([
      import('posthog-js'),
      import('react-router'),
      import('routes'),
      // Register the web-vitals and dead-click code the SDK would otherwise fetch from PostHog's asset host.
      import('posthog-js/dist/web-vitals'),
      import('posthog-js/dist/dead-clicks-autocapture'),
   ]);

   const routes = generateRoutes();

   const sanitizeEvent = createEventFilter({
      start,
      findRouteTemplate: (pathname) => matchRoutes(routes, pathname)?.reduce(appendRoutePath, '/') ?? null,
   });

   posthog.init(start.projectKey, { ...privacyConfig, before_send: sanitizeEvent });
}

function createEventFilter({ start, findRouteTemplate }: { start: AnalyticsStart; findRouteTemplate: (pathname: string) => string | null }) {
   function findTemplate(url: URL | null) {
      if (url?.origin !== start.origin) {
         return null;
      }

      return findRouteTemplate(url.pathname);
   }

   function toUrlTemplate(value: string) {
      const template = findTemplate(parseUrl(value));

      if (!template) {
         return null;
      }

      // Assigning the template through URL.pathname escapes an optional segment's '?' as %3F.
      const url = new URL(start.origin);
      url.pathname = template;

      return url.href;
   }

   function toPathTemplate(value: string) {
      const url = parseUrl(value, start.origin);

      return url?.pathname === value ? findTemplate(url) : null;
   }

   function groupByTemplate(heatmapData: z.output<typeof heatmapDataSchema>) {
      const pointsByTemplate = new Map<string, z.output<typeof heatmapPointSchema>[]>();

      for (const [url, points] of Object.entries(heatmapData)) {
         const template = toUrlTemplate(url);

         if (!template) {
            return null;
         }

         pointsByTemplate.set(template, [...(pointsByTemplate.get(template) ?? []), ...points]);
      }

      return Object.fromEntries(pointsByTemplate);
   }

   // A rewritten field that can't become a route template fails the parse, which drops the event.
   const rewrittenPropertiesSchema = z.object({
      $current_url: z.string().transform(toUrlTemplate).pipe(z.string()).optional(),
      $pathname: z.string().transform(toPathTemplate).pipe(z.string()).optional(),
      $prev_pageview_pathname: z.string().transform(toPathTemplate).pipe(z.string()).optional(),
      $referring_domain: z.string().regex(referringDomainPattern).optional().catch(undefined),
      $heatmap_data: heatmapDataSchema.transform(groupByTemplate).pipe(heatmapDataSchema).optional(),
   });

   return function sanitizeEvent({ uuid, event, timestamp, properties }: CaptureResult): CaptureResult | null {
      if (!allowedEvents.has(event)) {
         return null;
      }

      const rewritten = rewrittenPropertiesSchema.safeParse(properties);

      if (!rewritten.success) {
         return null;
      }

      const allowed = Object.fromEntries(Object.entries(properties).filter(([key]) => allowedProperties.has(key)));

      return { uuid, event, timestamp, properties: { ...allowed, ...rewritten.data, ...start.labels } };
   };
}

function appendRoutePath(template: string, { route: { path } }: { route: RouteObject }) {
   if (!path) {
      return template;
   }

   return path.startsWith('/') ? path : `${template.replace(/\/$/, '')}/${path}`;
}

function parseUrl(value: string, base?: string) {
   try {
      return new URL(value, base);
   } catch {
      return null;
   }
}
